import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promoOffers, promoCodes } from '@/mocks/promos';
import { useSavedPromos } from '@/hooks/useSavedPromos';
import { useToast } from '@/hooks/useToast';
import { useScrollReveal } from '@/hooks/useScrollReveal';

/* ── Countdown ── */
const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    d: Math.floor(diff / 86400000),
  };
};
const pad = (n: number) => String(n).padStart(2, '0');

const MiniTimer = ({ endsAt, color }: { endsAt: string; color: string }) => {
  const [t, setT] = useState(() => calcTime(endsAt));
  useEffect(() => {
    const id = setInterval(() => setT(calcTime(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return (
    <span className="font-mono text-[10px] tabular-nums" style={{ color }}>
      {t.d > 0 ? `${t.d}д ${pad(t.h)}:${pad(t.m)}:${pad(t.s)}` : `${pad(t.h)}:${pad(t.m)}:${pad(t.s)}`}
    </span>
  );
};

/* ── Promo Card ── */
const PromoCard = ({ offer, index }: { offer: typeof promoOffers[0]; index: number }) => {
  const { isSaved, toggleSave } = useSavedPromos();
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);
  const saved = isSaved(offer.id);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    const willSave = !saved;
    toggleSave(offer.id);
    showToast(
      willSave
        ? { type: 'promo_save', title: 'Акция сохранена!', message: offer.title, color: offer.color, link: '/profile', linkLabel: 'Посмотреть в профиле' }
        : { type: 'promo_remove', title: 'Акция удалена из закладок', message: offer.title }
    );
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (offer.code) {
      navigator.clipboard.writeText(offer.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const daysLeft = Math.ceil((new Date(offer.endsAt).getTime() - Date.now()) / 86400000);
  const isUrgent = daysLeft <= 3;

  return (
    <Link
      to={`/promos/${offer.id}`}
      className="group relative rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-all duration-300"
      style={{
        border: `1px solid ${offer.color}22`,
        background: 'rgba(255,255,255,0.02)',
        minHeight: 320,
      }}
    >
      {/* Top accent */}
      <div
        className="h-0.5 w-full shrink-0"
        style={{ background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary}, transparent)` }}
      />

      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={offer.image}
          alt={offer.title}
          className="w-full h-full object-cover object-top opacity-12 transition-transform duration-700 group-hover:scale-105"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(160deg, rgba(5,5,8,0.92) 0%, rgba(5,5,8,0.65) 60%, rgba(5,5,8,0.4) 100%)' }}
        />
        <div
          className="absolute top-0 right-0 w-48 h-48 opacity-15 rounded-full"
          style={{ background: offer.color, filter: 'blur(70px)', transform: 'translate(30%,-30%)' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 p-5 flex flex-col flex-1">
        {/* Top row: badge + save */}
        <div className="flex items-start justify-between gap-2 mb-4">
          <span
            className="px-2.5 py-1 rounded-full font-mono text-[9px] font-bold tracking-wider"
            style={{ background: offer.badgeColor, color: '#050508', boxShadow: `0 0 8px ${offer.badgeColor}` }}
          >
            {offer.badge}
          </span>
          <button
            onClick={handleSave}
            className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200 shrink-0"
            style={{
              background: saved ? `${offer.color}20` : 'rgba(255,255,255,0.06)',
              border: saved ? `1px solid ${offer.color}45` : '1px solid rgba(255,255,255,0.1)',
            }}
            title={saved ? 'Удалить из закладок' : 'Сохранить акцию'}
          >
            <i
              className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}
              style={{ color: saved ? offer.color : 'rgba(255,255,255,0.4)', fontSize: 14 }}
            />
          </button>
        </div>

        {/* Discount + title */}
        <div className="flex items-start gap-4 mb-3">
          <div
            className="w-14 h-14 flex items-center justify-center rounded-2xl font-orbitron font-black text-lg shrink-0"
            style={{
              background: `${offer.color}18`,
              border: `2px solid ${offer.color}`,
              color: offer.color,
              boxShadow: `0 0 16px ${offer.color}30`,
            }}
          >
            {offer.discount}
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="font-orbitron font-bold text-sm text-white leading-snug mb-1 group-hover:opacity-80 transition-opacity"
            >
              {offer.title}
            </h3>
            <p className="font-mono text-xs" style={{ color: offer.color }}>{offer.subtitle}</p>
          </div>
        </div>

        <p className="font-mono text-xs text-white/35 leading-relaxed line-clamp-2 mb-4">
          {offer.description}
        </p>

        {/* Timer row */}
        <div className="flex items-center gap-2 mb-4">
          <div
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{
              background: isUrgent ? 'rgba(255,45,120,0.12)' : `${offer.color}10`,
              border: `1px solid ${isUrgent ? 'rgba(255,45,120,0.3)' : `${offer.color}25`}`,
            }}
          >
            <i
              className={isUrgent ? 'ri-alarm-warning-line' : 'ri-time-line'}
              style={{ color: isUrgent ? '#ff2d78' : offer.color, fontSize: 11 }}
            />
            <span className="font-mono text-[10px] text-white/40">До конца:</span>
            <MiniTimer endsAt={offer.endsAt} color={isUrgent ? '#ff2d78' : offer.color} />
          </div>
          {isUrgent && (
            <span
              className="px-2 py-1 rounded-lg font-mono text-[9px] font-bold"
              style={{ background: 'rgba(255,45,120,0.15)', color: '#ff2d78', border: '1px solid rgba(255,45,120,0.3)' }}
            >
              СРОЧНО
            </span>
          )}
        </div>

        {/* Bottom: promo code + CTA */}
        <div className="mt-auto flex items-center gap-2">
          {offer.code ? (
            <button
              onClick={handleCopy}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200 flex-1"
              style={
                copied
                  ? { background: '#39ff14', color: '#050508' }
                  : { border: `1px dashed ${offer.color}40`, color: offer.color, background: `${offer.color}06` }
              }
            >
              <i className={copied ? 'ri-check-line' : 'ri-coupon-line'} />
              {copied ? 'Скопировано!' : `Промокод: ${offer.code}`}
            </button>
          ) : (
            <div className="flex-1" />
          )}
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 transition-all duration-300 group-hover:translate-x-0.5"
            style={{ background: `${offer.color}18`, border: `1px solid ${offer.color}30` }}
          >
            <i className="ri-arrow-right-line text-sm" style={{ color: offer.color }} />
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ── Hot Promo Code Pill ── */
const HotCodePill = ({ code }: { code: typeof promoCodes[0] }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full cursor-pointer transition-all duration-200 shrink-0"
      style={
        copied
          ? { background: '#39ff14', color: '#050508' }
          : { background: `${code.color}10`, border: `1px solid ${code.color}30`, color: code.color }
      }
    >
      <i className={`${code.icon} text-xs`} />
      <span className="font-mono text-xs font-bold">{code.code}</span>
      <span className="font-mono text-[10px] opacity-60">{code.discount}</span>
      {code.isHot && !copied && (
        <span
          className="px-1.5 py-0.5 rounded-full font-mono text-[8px] font-bold"
          style={{ background: '#ff2d78', color: '#050508' }}
        >
          HOT
        </span>
      )}
      {copied && <i className="ri-check-line text-xs" />}
    </button>
  );
};

/* ── Main Section ── */
const HomePromoSection = () => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.08 });
  const hotCodes = promoCodes.filter((c) => c.isHot || c.isNew).slice(0, 5);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="py-20 px-6 lg:px-12 relative"
      style={{ background: '#050508' }}
    >
      {/* Background glows */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: '#ff2d78', filter: 'blur(120px)' }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-4 pointer-events-none"
        style={{ background: '#00f5ff', filter: 'blur(100px)' }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl"
                style={{ background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.35)' }}
              >
                <i className="ri-price-tag-3-line text-sm" style={{ color: '#ff2d78' }} />
              </div>
              <span
                className="font-mono text-xs tracking-[0.3em] uppercase"
                style={{ color: 'rgba(255,45,120,0.7)' }}
              >
                Специальные предложения
              </span>
            </div>
            <h2 className="font-orbitron font-black">
              <span className="block text-3xl md:text-5xl text-white">РЕКОМЕНДУЕМЫЕ</span>
              <span
                className="block text-3xl md:text-5xl"
                style={{ color: '#ff2d78', textShadow: '0 0 25px rgba(255,45,120,0.5)' }}
              >
                АКЦИИ
              </span>
            </h2>
            <p className="font-mono text-sm text-white/35 mt-2 max-w-md">
              Лучшие предложения прямо сейчас — скидки до 40%, промокоды и flash-распродажи
            </p>
          </div>

          <Link
            to="/promos"
            className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300 self-start md:self-auto"
            style={{ border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78', background: 'rgba(255,45,120,0.06)' }}
          >
            Все акции
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Promo cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {promoOffers.map((offer, i) => (
            <div
              key={offer.id}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.1}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.1}s`,
              }}
            >
              <PromoCard offer={offer} index={i} />
            </div>
          ))}
        </div>

        {/* Hot promo codes strip */}
        <div
          className="rounded-2xl p-5"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.07)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.45s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.45s',
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg"
                style={{ background: 'rgba(240,255,0,0.12)', border: '1px solid rgba(240,255,0,0.25)' }}
              >
                <i className="ri-coupon-3-line text-sm" style={{ color: '#f0ff00' }} />
              </div>
              <div>
                <p className="font-orbitron font-bold text-xs text-white">Горячие промокоды</p>
                <p className="font-mono text-[10px] text-white/35">Нажмите чтобы скопировать</p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap flex-1">
              {hotCodes.map((code) => (
                <HotCodePill key={code.id} code={code} />
              ))}
            </div>

            <Link
              to="/promos/search"
              className="whitespace-nowrap flex items-center gap-1.5 font-mono text-xs cursor-pointer transition-colors shrink-0"
              style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#f0ff00'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }}
            >
              Все промокоды
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomePromoSection;